<template>
  <div class="location-search-container">
    <!-- Search Input -->
    <div class="relative">
      <div
        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
      </div>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="placeholder"
        class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        @input="onSearchInput"
        @focus="onInputFocus"
        @blur="onInputBlur"
      />
      <!-- Loading indicator -->
      <div
        v-if="isSearching"
        class="absolute right-3 top-1/2 transform -translate-y-1/2"
      >
        <div
          class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
        ></div>
      </div>
    </div>

    <!-- Search Suggestions -->
    <div
      v-if="showSuggestions && searchSuggestions.length > 0"
      class="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto mt-1"
    >
      <div
        v-for="(suggestion, index) in searchSuggestions"
        :key="index"
        @mousedown="selectSuggestion(suggestion)"
        class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-5 h-5 rounded-full flex-shrink-0 mt-0.5"
            :class="getSuggestionIconClass(suggestion.source)"
          >
            <svg
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-gray-900">
              {{ suggestion.mainText }}
            </div>
            <div class="text-sm text-gray-500 truncate">
              {{ suggestion.secondaryText }}
            </div>
            <div v-if="suggestion.source" class="text-xs text-gray-400 mt-1">
              Source: {{ getSuggestionSourceLabel(suggestion.source) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Current Location Button -->
    <button
      v-if="showCurrentLocationButton"
      @click="getCurrentLocation"
      :disabled="isGettingLocation"
      class="mt-3 w-full p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:bg-blue-300 transition-colors flex items-center justify-center gap-2"
    >
      <div
        v-if="isGettingLocation"
        class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
      ></div>
      <svg
        v-else
        class="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        ></path>
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        ></path>
      </svg>
      {{ isGettingLocation ? 'Localisation...' : currentLocationText }}
    </button>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { addressSearchService } from '@features/map/utils/searchUtils.js'
import {
  getCurrentPositionWithFallback,
  handleLocationError,
  DEFAULT_LOCATION,
} from '@features/map/utils/locationUtils.js'
import { getAddressWithFallback } from '@features/map/utils/searchUtils.js'
import { GoogleMapUtils } from '@features/map/uitls/googleMap/googleMapUtils.js'

// Props
const props = defineProps({
  placeholder: {
    type: String,
    default: 'Rechercher une adresse...',
  },
  showCurrentLocationButton: {
    type: Boolean,
    default: true,
  },
  currentLocationText: {
    type: String,
    default: 'Utiliser ma position actuelle',
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  debounceMs: {
    type: Number,
    default: 300,
  },
  countryRestrictions: {
    type: Array,
    default: () => ['bj', 'tg', 'ng'], // Bénin + pays voisins
  },
})

// Emits
const emit = defineEmits([
  'locationSelected',
  'searchStart',
  'searchEnd',
  'error',
  'suggestionAvailable',
])

// Refs
const searchInput = ref(null)
const searchQuery = ref('')
const searchSuggestions = ref([])
const showSuggestions = ref(false)
const isSearching = ref(false)
const isGettingLocation = ref(false)

// State
let searchTimeout = null
let isInitialized = false

// Lifecycle
onMounted(async () => {
  await initializeService()
  if (props.autoFocus) {
    await nextTick()
    searchInput.value?.focus()
  }
})

onUnmounted(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})

// Methods
async function initializeService() {
  try {
    await addressSearchService.initialize()
    isInitialized = true
    console.log('✅ LocationSearch service initialized')
  } catch (error) {
    console.warn('LocationSearch initialization failed:', error)
    emit('error', {
      type: 'initialization',
      message: 'Service de recherche indisponible',
      error,
    })
  }
}

function onSearchInput() {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchSuggestions.value = []
    showSuggestions.value = false
    return
  }

  // Debounce search
  searchTimeout = setTimeout(() => {
    performSearch()
  }, props.debounceMs)
}

async function performSearch() {
  if (!isInitialized) {
    await initializeService()
  }

  isSearching.value = true
  emit('searchStart', searchQuery.value)

  try {
    const suggestions = await addressSearchService.searchSuggestions(
      searchQuery.value,
    )
    searchSuggestions.value = suggestions
    showSuggestions.value = true
    console.log(
      `🔍 Found ${suggestions.length} suggestions for "${searchQuery.value}"`,
    )
  } catch (error) {
    console.error('Search error:', error)
    searchSuggestions.value = []
    showSuggestions.value = false
    emit('error', {
      type: 'search',
      message: 'Erreur lors de la recherche',
      error,
    })
  } finally {
    isSearching.value = false
    emit('searchEnd', searchQuery.value)
  }
}

async function selectSuggestion(suggestion) {
  try {
    showSuggestions.value = false
    searchQuery.value = suggestion.description

    const location = await addressSearchService.selectSuggestion(suggestion)
    console.log('✅ Location selected:', location)

    emit('locationSelected', {
      ...location,
      suggestion,
    })
  } catch (error) {
    console.error('Error selecting suggestion:', error)
    emit('error', {
      type: 'selection',
      message: 'Impossible de sélectionner cette position',
      error,
    })
  }
}

async function getCurrentLocation() {
  if (isGettingLocation.value) return

  isGettingLocation.value = true

  try {
    const onProgressiveUpdate = async (refinedPos) => {
      try {
        const details = await getAddressWithFallback(refinedPos, GoogleMapUtils)
        emit('locationSelected', {
          coordinates: refinedPos,
          address: details?.formatted_address || 'Ma position actuelle',
          locationText: details?.formatted_address || 'Ma position actuelle',
          details,
          source: 'current_location',
        })
      } catch (e) {
        console.warn('Progressive geocoding error:', e)
      }
    }

    const position = await getCurrentPositionWithFallback({
      preferCache: true,
      onProgressiveUpdate
    })

    const location = {
      coordinates: position,
      address: 'Ma position actuelle',
      locationText: 'Ma position actuelle',
      details: await getAddressWithFallback(position, GoogleMapUtils),
    }

    console.log('✅ Current location obtained:', location)

    emit('locationSelected', {
      ...location,
      source: 'current_location',
    })

    // Update search query
    searchQuery.value = location.address
    searchSuggestions.value = []
    showSuggestions.value = false
  } catch (error) {
    console.error('Error getting current location:', error)
    handleLocationError(error)
    emit('error', {
      type: 'location',
      message: 'Impossible de récupérer votre position',
      error,
    })
  } finally {
    isGettingLocation.value = false
  }
}

function onInputFocus() {
  if (searchSuggestions.value.length > 0) {
    showSuggestions.value = true
  }
}

function onInputBlur() {
  // Delay hiding to allow click on suggestions
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Utility functions
function getSuggestionIconClass(source) {
  switch (source) {
    case 'google':
      return 'bg-blue-500'
    case 'nominatim':
      return 'bg-green-500'
    default:
      return 'bg-gray-500'
  }
}

function getSuggestionSourceLabel(source) {
  switch (source) {
    case 'google':
      return 'Google Maps'
    case 'nominatim':
      return 'OpenStreetMap'
    default:
      return 'Inconnue'
  }
}

watch(
  () => searchSuggestions.value,
  newVal => {
    emit('suggestionAvailable', newVal)
  },
)

// Public methods
defineExpose({
  focus: () => searchInput.value?.focus(),
  clear: () => {
    searchQuery.value = ''
    searchSuggestions.value = []
    showSuggestions.value = false
  },
  search: query => {
    searchQuery.value = query
    performSearch()
  },
  selectSuggestion,
})
</script>

<style scoped>
.location-search-container {
  position: relative;
}

/* Custom scrollbar for suggestions */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
